import { useEffect, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import api from '../services/api';
import { Button, Input, Modal, Badge, TableActionButtons, EditIcon, DeleteIcon, DataTablePagination, DataTableCard } from '../components/ui';
import { DATA_TABLE_THEME, dataTableCustomStyles } from '../utils/dataTableTheme';

interface UserItem {
  id: string;
  username: string;
  email: string;
  role: string;
}

export default function Users() {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [pending, setPending] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createData, setCreateData] = useState({ username: '', email: '', password: '', role: 'admin' });
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editData, setEditData] = useState<UserItem | null>(null);

  const fetchUsers = () => {
    setPending(true);
    api.get('/admin/users')
      .then(r => setUsers(r.data.filter((u: any) => u.role === 'admin')))
      .catch(() => {})
      .finally(() => setPending(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateSaving(true);
    try {
      await api.post('/admin/users', createData);
      setCreateOpen(false);
      setCreateData({ username: '', email: '', password: '', role: 'admin' });
      fetchUsers();
    } catch {
      alert('Error al crear usuario');
    } finally { setCreateSaving(false); }
  };

  const handleEditOpen = (u: UserItem) => { setEditData(u); setEditOpen(true); };
  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editData) return;
    setEditSaving(true);
    try {
      await api.put(`/admin/users/${editData.id}`, { username: editData.username, email: editData.email, role: editData.role });
      setEditOpen(false);
      setEditData(null);
      fetchUsers();
    } catch {
      alert('Error al actualizar usuario');
    } finally { setEditSaving(false); }
  };

  const handleDelete = async (u: UserItem) => {
    if (!window.confirm('¿Eliminar usuario?')) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      fetchUsers();
    } catch {
      alert('Error al eliminar usuario');
    }
  };

  const columns: TableColumn<UserItem>[] = [
    { name: 'ID', selector: r => r.id, sortable: true, wrap: true },
    { name: 'Usuario', selector: r => r.username, sortable: true },
    { name: 'Email', selector: r => r.email, sortable: true },
    { name: 'Rol', cell: r => <Badge variant="script">{r.role}</Badge>, sortable: true },
    {
      name: 'Acciones',
      cell: r => (
        <TableActionButtons
          actions={[
            { label: 'Editar', icon: <EditIcon />, variant: 'edit', onClick: () => handleEditOpen(r) },
            { label: 'Eliminar', icon: <DeleteIcon />, variant: 'delete', onClick: () => handleDelete(r) },
          ]}
        />
      ),
      ignoreRowClick: true,
    },
  ];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-2xl font-bold text-gray-100">Usuarios Admin</h1>
        <Button onClick={() => setCreateOpen(true)} leftIcon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        }>
          Nuevo usuario
        </Button>
      </div>

      <DataTableCard>
      <DataTable
        columns={columns}
        data={users}
        theme={DATA_TABLE_THEME}
        customStyles={dataTableCustomStyles}
        paginationComponent={DataTablePagination}
        progressPending={pending}
        pagination
        highlightOnHover
        responsive
        noDataComponent={<div className="py-12 text-center text-gray-500">No hay usuarios</div>}
      />
      </DataTableCard>

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="Crear usuario admin">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input label="Usuario" placeholder="Usuario" value={createData.username} onChange={e => setCreateData(d => ({ ...d, username: e.target.value }))} required />
          <Input label="Email" type="email" placeholder="Email" value={createData.email} onChange={e => setCreateData(d => ({ ...d, email: e.target.value }))} required />
          <Input label="Contraseña" type="password" placeholder="Contraseña" value={createData.password} onChange={e => setCreateData(d => ({ ...d, password: e.target.value }))} required />
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" fullWidth onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button type="submit" fullWidth loading={createSaving}>Crear</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={editOpen} onClose={() => { setEditOpen(false); setEditData(null); }} title="Editar usuario">
        {editData && (
          <form onSubmit={handleEditSave} className="space-y-4">
            <Input label="Usuario" value={editData.username} onChange={e => setEditData({ ...editData, username: e.target.value })} required />
            <Input label="Email" type="email" value={editData.email} onChange={e => setEditData({ ...editData, email: e.target.value })} required />
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="ghost" fullWidth onClick={() => { setEditOpen(false); setEditData(null); }}>Cancelar</Button>
              <Button type="submit" variant="secondary" fullWidth loading={editSaving}>Guardar</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
